import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Truck } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { acquisitionsApi } from '@/services/api/endpoints/library';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Supplier } from '@/types/library';
import { orDash } from '@/utils/format';
import { Badge } from './shared';

/**
 * Booksellers the school buys from.
 *
 * <p>Retired, never deleted. Past orders name their supplier, and removing one
 * from the database takes the purchase history of every book they sold with it.
 */
export function SuppliersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.LIBRARY_SUPPLIER_MANAGE);

  const [editing, setEditing] = useState<Supplier | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');

  const suppliers = useQuery({
    queryKey: queryKeys.acquisitions.suppliers(false),
    queryFn: () => acquisitionsApi.suppliers(false),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.acquisitions.all });

  function open(supplier: Supplier | null) {
    setEditing(supplier);
    setAdding(supplier === null);
    setName(supplier?.name ?? '');
    setContactPerson(supplier?.contactPerson ?? '');
    setEmail(supplier?.email ?? '');
    setPhone(supplier?.phone ?? '');
    setCity(supplier?.city ?? '');
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        contactPerson: contactPerson.trim() === '' ? undefined : contactPerson.trim(),
        email: email.trim() === '' ? undefined : email.trim(),
        phone: phone.trim() === '' ? undefined : phone.trim(),
        city: city.trim() === '' ? undefined : city.trim(),
      };
      return editing === null
        ? acquisitionsApi.createSupplier(payload)
        : acquisitionsApi.updateSupplier(editing.id, payload);
    },
    onSuccess: (supplier) => {
      toast.success(`${supplier.name} saved`);
      setAdding(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, next }: { id: string; next: 'ACTIVE' | 'INACTIVE' }) =>
      acquisitionsApi.setSupplierStatus(id, next),
    onSuccess: (supplier) => {
      toast.success(
        supplier.status === 'ACTIVE'
          ? `${supplier.name} is back in use`
          : `${supplier.name} retired — their past orders are untouched`,
      );
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<Supplier>[] = [
    {
      key: 'name',
      header: 'Supplier',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{row.name}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.city)}</span>
        </span>
      ),
    },
    { key: 'contact', header: 'Contact', render: (row) => orDash(row.contactPerson) },
    {
      key: 'reach',
      header: 'Reach them',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.email)}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.phone)}</span>
        </span>
      ),
    },
    {
      key: 'orders',
      header: 'Orders',
      className: 'text-right',
      render: (row) => row.acquisitionCount,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge tone={row.status === 'ACTIVE' ? 'success' : 'neutral'}>
          {row.status === 'ACTIVE' ? 'in use' : 'retired'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setStatus.mutate({
                  id: row.id,
                  next: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                })
              }
            >
              {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="Suppliers"
        description="Who the school buys books from."
        icon={<Truck className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => open(null)}>Add a supplier</Button> : undefined}
      />

      <Card>
        <DataTable
          columns={columns}
          rows={suppliers.data}
          rowKey={(row) => row.id}
          loading={suppliers.isPending}
          error={suppliers.error}
          onRetry={() => void suppliers.refetch()}
          emptyTitle="No suppliers"
          emptyDescription="Add the booksellers the school orders from."
        />
      </Card>

      <Modal
        open={adding || editing !== null}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        title={editing === null ? 'Add a supplier' : editing.name}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setAdding(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={name.trim() === '' || save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} required />
          <TextField
            label="Who to ask for"
            value={contactPerson}
            onChange={(event) => setContactPerson(event.target.value)}
          />
          <TextField label="Email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
          <TextField label="Phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
          <TextField label="City" value={city} onChange={(event) => setCity(event.target.value)} />
        </div>
      </Modal>
    </>
  );
}
